import classnames from 'classnames';
import React from 'react';

import { IconGradientCheck } from '../../spaces-bundle/components/Icons/Icons';
import PricesSeparator from '../icon/prices-separator';
import { IRON } from '@wetransfer/transfer/constant/colors';
import PAYLOAD_PROPS from '@wetransfer/transfer/constant/experiments/splash-page-discount-label';
import Helper from '@wetransfer/transfer/helper';
import I18n from '@wetransfer/transfer/modules/i18n';
import { IconCheckmarkThin } from '@wetransfer/transfer/spaces-bundle/components/Icons/Icons';

import { PricingPanelButton } from './PricingPanelButton';

import './PricingPanel.scss';

interface Pricing {
  frequency?: string;
  price: string;
}

interface USP {
  usp: string;
  check?: boolean;
}

interface CTA {
  text: string;
  href: string;
  onClick?(event?: React.MouseEvent): void;
}

export interface Props {
  colorScheme: 'Light' | 'Dark' | 'Transparent' | 'Medium';
  title: string;
  description?: string;
  pricing?: Pricing[];
  features: USP[];
  cta: CTA;
  gradient?: boolean;
  isProPanel?: boolean;
  isClassicVariant?: boolean;
  additionalClass?: string;
  showHeaderLabel?: boolean;
  payload?: Array<PAYLOAD_PROPS>;
}

export const PricingPanel: React.FC<Props> = ({
  children,
  colorScheme = 'Light',
  title,
  description,
  pricing,
  features,
  cta,
  gradient,
  isProPanel = false,
  isClassicVariant = false,
  additionalClass,
  showHeaderLabel = false,
  payload,
}): JSX.Element => {
  const colorSchemeIsDark = colorScheme === 'Dark';
  const colorSchemeIsLight = colorScheme === 'Light';
  const colorSchemeIsTransparent = colorScheme === 'Transparent';
  const colorSchemeIsMedium = colorScheme === 'Medium';

  const discountData = Helper.getCurrentExperimentData(payload);

  const experimentLabel = I18n.t('discount.pricing_page.pill_label', {
    coupon_code_name: discountData?.campaignTitle,
    discount_amount: discountData?.percentageDiscount,
  });

  const hasFeatureCheckIcon = () => {
    return features.some((feature) => feature.check);
  };

  const renderHeaderSection = () => {
    if (isClassicVariant) {
      return (
        <div className="PricingPanel__card-header">
          <h2
            className="PricingPanel__card-title"
            dangerouslySetInnerHTML={{ __html: title }}
          />
          {description && (
            <h3 className="PricingPanel__card-description">{description}</h3>
          )}
        </div>
      );
    }

    return (
      <>
        <h2
          className="PricingPanel__card-title"
          dangerouslySetInnerHTML={{ __html: title }}
        />
        {description && (
          <h3 className="PricingPanel__card-description">{description}</h3>
        )}
      </>
    );
  };

  const getCheckmark = () => {
    if (isClassicVariant) {
      return <IconGradientCheck aria-hidden="true" focusable="false" />;
    }

    return <IconCheckmarkThin aria-hidden="true" focusable="false" />;
  };

  const renderExperimentLabel = () => {
    return (
      <div className="PricingPanel__experiment-header">
        <span className="PricingPanel__experiment-header--title">
          {experimentLabel}
        </span>
      </div>
    );
  };

  return (
    <div
      className={classnames('PricingPanel', {
        'Columns-1': !children,
        'Columns-2': children,
        'PricingPanel--light': colorSchemeIsLight,
        'PricingPanel--dark': colorSchemeIsDark,
        'PricingPanel--transparent': colorSchemeIsTransparent,
        'PricingPanel--gradient': gradient,
        'PricingPanel--medium': colorSchemeIsMedium,
        [additionalClass]: additionalClass,
      })}
    >
      {showHeaderLabel && renderExperimentLabel()}
      <div className="PricingPanel__card-information">
        <div className="PricingPanel__card Columns-1">
          {renderHeaderSection()}
          {pricing ? (
            <div className="PricingPanel__pricing-wrapper">
              <>
                <div className="PricingPanel__pricing">
                  <div className="PricingPanel__pricing-amount">
                    {pricing[0].price}
                  </div>
                  <div className="PricingPanel__pricing-subtitle">
                    {pricing[0].frequency}
                  </div>
                  {/* The new Pro classic pricing panel should not render the per person text */}
                  {pricing[0].frequency && (
                    <div className="PricingPanel__pricing-subtitle PricingPanel__pricing-subtitle--teams">
                      {I18n.t('page.pricing.pro.per_person')}
                    </div>
                  )}
                </div>
                {pricing[1] ? (
                  <>
                    <PricesSeparator
                      opacity={1}
                      color={IRON}
                      className="PricingPanel__pricing-separator"
                    />
                    <div className="PricingPanel__pricing">
                      <div className="PricingPanel__pricing-amount">
                        {pricing[1].price}
                      </div>
                      <div className="PricingPanel__pricing-subtitle">
                        {pricing[1].frequency}
                      </div>
                      <div className="PricingPanel__pricing-subtitle PricingPanel__pricing-subtitle--teams">
                        {I18n.t('page.pricing.pro.per_person')}
                      </div>
                    </div>
                  </>
                ) : null}
              </>
            </div>
          ) : null}
          <ul
            className={classnames('PricingPanel__features-list', {
              'PricingPanel__features-list--checklist': hasFeatureCheckIcon(),
            })}
          >
            {features.map((feature, index) => (
              <li key={index}>
                {feature.check ? getCheckmark() : null}
                <p dangerouslySetInnerHTML={{ __html: feature.usp }} />
              </li>
            ))}
          </ul>
          {cta && (
            <PricingPanelButton
              cta={cta}
              isProPanel={isProPanel}
              colorScheme={colorScheme}
              isClassicVariant={isClassicVariant}
            />
          )}
        </div>
        {children ? (
          <div className="PricingPanel__products Columns-1">{children}</div>
        ) : null}
      </div>
    </div>
  );
};
